/** biome-ignore-all lint/suspicious/noExplicitAny: <add types after refactoring> */
import { type FC, Fragment, type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Form, type FormItemProps, type RefSelectProps, Select, type SelectProps, Spin, type TooltipProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useDebounce } from 'use-debounce';

import { ENV_VARS } from '@/env';

import { isRequiredField } from '../../../utilz';
import { routeFileSelector } from '../../../utilz/i18n';
import formLabelToolTip from '../Common/formLabelToolTip';
import FormNativeSelectBox from './FormNativeSelectBox';
import HiddenFormInput from './HiddenFormInput';

const { Option } = Select;

let popupTimeout: ReturnType<typeof setTimeout> | false = false;

const isHBUser = ENV_VARS.REACT_APP_THEME === 'halliburton';

export type Accessors = {
  label: string;
  value: string;
};

interface FormSelectBoxProps extends SelectProps, Pick<FormItemProps, 'rules' | 'colon'> {
  label?: string;
  margin?: string;
  name?: string;
  options?: DefaultOptionType[];
  inverMode?: boolean;
  singleLineMode?: boolean;
  editMode?: boolean;
  asyncFetchMode?: boolean;
  asyncFunction?: (params: Record<string, unknown>) => Promise<{ data: any }>;
  searchParamName?: string;
  defaultParams?: Record<string, unknown>;
  accessors?: Accessors;
  CustomOptionsComponent?: FC<{ data: any; accessors: Accessors }>;
  hasError?: boolean;
  userType?: string;
  defaultOptions?: any[];
  setRecord?: string;
  labelWithValue?: boolean;
  handleResponseData?: (data: any) => any[];
  searchParamMinLength?: number;
  disableSearchInput?: boolean;
  labelClass?: string;
  dataTestId?: string;
  shouldUseNativeSelect?: boolean;
  tooltip?: TooltipProps['title'];
  disableValidation?: boolean;
  disableOpen?: boolean;
  isRemoved?: boolean;
  // TODO: investigate why value here has an unknown type
  onChange?: (value?: unknown) => void;
  isRequired?: boolean;
}

const FormSelectBox: FC<FormSelectBoxProps> = ({
  placeholder,
  label,
  options = [],
  defaultValue,
  margin = 'mb-16',
  loading = false,
  name = `testFiled-${Math.random()}`,
  rules = [],
  inverMode = false,
  singleLineMode = false,
  className = '',
  editMode = true,
  colon = false,
  disabled = false,
  onChange = () => {},
  asyncFetchMode = false,
  asyncFunction,
  searchParamName = 'SearchValue',
  defaultParams = {},
  value = undefined,
  accessors = { label: 'label', value: 'value' },
  CustomOptionsComponent,
  allowClear = true,
  hasError = false,
  onClear = false,
  mode,
  tooltip,
  userType = '',
  defaultOptions = [],
  setRecord = '',
  id = undefined,
  suffixIcon = undefined,
  tabIndex,
  disableValidation = false,
  disableOpen = false,
  isRemoved = false,
  labelWithValue = false,
  handleResponseData = () => [],
  searchParamMinLength = 1,
  disableSearchInput = false,
  labelClass = '',
  dataTestId = '',
  showSearch = true,
  shouldUseNativeSelect = false,
  labelInValue = false,
  isRequired,
  ...rest
}) => {
  const isFieldRequired = ((rules.length > 0 && isRequiredField(rules) && !disableValidation) || isRequired) ?? false;

  const [dataOptions, setDataOptions] = useState(defaultOptions);
  const [dataLoading, setDataLoading] = useState(false);
  const [search, setSearch] = useState<string | undefined>();
  const [searchUsed, setSearchUsed] = useState(defaultOptions?.length > 0);
  const [searchDebounced] = useDebounce(search, 600);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const selectRef: RefObject<RefSelectProps | null> = useRef(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: we need refactor fetchData to use it inside useEffect
  useEffect(() => {
    if (asyncFetchMode && searchDebounced) {
      setSearchUsed(true);
      fetchData(searchDebounced ? searchDebounced : undefined);
    }
  }, [searchDebounced, asyncFetchMode]);

  const fetchData = async (search?: string) => {
    if (!asyncFunction) {
      return;
    }
    try {
      setDataLoading(true);
      let dataValue = [];
      if (setRecord === 'shipperConsignee') {
        const { data } = await asyncFunction({
          ...defaultParams,
          name: search,
        });
        dataValue = data?.totalData;
      } else if (userType === 'CARR') {
        const { data } = await asyncFunction({
          ...defaultParams,
          name: search,
        });
        dataValue = data?.totalData;
      } else {
        const { data } = await asyncFunction({
          ...defaultParams,
          [searchParamName]: search,
        });
        dataValue = data;
      }

      if (Array.isArray(dataValue)) {
        setDataOptions(dataValue);
      } else {
        setDataOptions(handleResponseData(dataValue));
      }
      setDataLoading(false);
    } catch (error: any) {
      if (error.code !== 'ERR_CANCELED') {
        setDataOptions([]);
        setDataLoading(false);
      }
    }
  };

  const handleChange: SelectProps['onChange'] = (val) => {
    try {
      if (mode === 'multiple') {
        if (popupTimeout) {
          clearTimeout(popupTimeout);
          popupTimeout = false;
        }
        if (selectRef?.current && val?.length) {
          popupTimeout = setTimeout(() => {
            selectRef?.current?.blur();
          }, 2000);
        }
      }

      if (labelWithValue === true) {
        const data = asyncFetchMode ? dataOptions : options;
        const findMatchOptions = data.filter((i) => val.includes(i.value));
        onChange(findMatchOptions);
      } else if (isRemoved === true) {
        onChange(val);
      } else {
        const data = asyncFetchMode ? dataOptions : options;
        onChange(data.find((item) => item[accessors.value] === val));
      }
    } catch {
      onChange();
    }
  };

  const handleSearchValueChange: SelectProps['onSearch'] = (val) => {
    if (String(val ? val : '').length >= searchParamMinLength) {
      setSearch(val);
    }
  };

  return !shouldUseNativeSelect ? (
    <Form.Item
      tooltip={tooltip ? formLabelToolTip(tooltip) : null}
      className={`${inverMode ? 'invert-mode' : ''} ${margin} ${className} ${
        singleLineMode ? 'single-line-mode' : ''
      } form-input-container common-select-box-container ${
        !editMode ? 'edit-mode-off' : ''
      } ${isHBUser ? 'hb-select-box' : ''}`}
      data-testid={dataTestId}
      {...(label
        ? {
            label: (
              <span className={`${isFieldRequired ? 'fw-700' : ''} ${labelClass}`}>
                {label}
                {isFieldRequired && <span className="required">*</span>}
              </span>
            ),
          }
        : {})}
      colon={colon}
      rules={disableValidation ? [] : rules}
      {...(!asyncFetchMode ? { name } : {})}
    >
      <Select
        ref={selectRef}
        // @ts-expect-error TODO: investigate this prop
        autoComplete="off"
        id={id || name}
        mode={mode}
        defaultValue={mode === 'multiple' ? [] : defaultValue}
        {...(onClear ? { onClear } : {})}
        variant="borderless"
        disabled={disabled}
        loading={asyncFetchMode ? dataLoading : loading}
        placeholder={editMode ? placeholder : ''}
        className={`form-select-box ${
          hasError ? 'ant-select-status-error' : ''
        } ${suffixIcon ? 'has-suffix-icon' : ''}`}
        // @ts-expect-error
        showSearch={editMode && showSearch}
        onChange={handleChange}
        labelInValue={labelInValue}
        allowClear={!editMode ? false : allowClear}
        suffixIcon={!editMode ? null : suffixIcon}
        {...(!editMode
          ? {
              clearIcon: false,
              open: false,
            }
          : {})}
        // Async Search Logics
        {...(!asyncFetchMode
          ? {
              filterOption: (input, option) =>
                String(option?.children ? option?.children : '')
                  ?.toLowerCase()
                  .includes(input?.toLowerCase()),
              optionFilterProp: 'children',
            }
          : { filterOption: false, onSearch: handleSearchValueChange, value })}
        {...rest}
        {...(tabIndex ? { tabIndex } : {})}
        {...(disableOpen ? { open: false, showSearch: false } : {})}
        {...(disableSearchInput ? { searchValue: '' } : {})}
      >
        {!dataLoading ? (
          (CustomOptionsComponent
            ? (asyncFetchMode ? dataOptions : options).map((row) => (
                <Option value={row[accessors.value]} key={row[accessors.value]}>
                  <CustomOptionsComponent data={row} accessors={accessors} key={row[accessors.value]} />
                </Option>
              ))
            : asyncFetchMode
              ? dataOptions
              : options
          ).map((row) => (
            <Option value={row[accessors.value]} key={row[accessors.value]}>
              {row[accessors.label]}
            </Option>
          ))
        ) : (
          <Option disabled value="">
            <div className="d-flex justify-content-center p-20">
              <Spin />
            </div>
          </Option>
        )}
        {asyncFetchMode && !searchUsed ? (
          <Option disabled value="">
            <span className="f-12 text-grey-1">{t('translation:default.notification.emptySelect')}</span>{' '}
          </Option>
        ) : (
          <Fragment />
        )}
      </Select>
    </Form.Item>
  ) : (
    <div className={`${margin}`}>
      <Form.Item dependencies={[name]} className="m-0">
        {(form) => (
          <FormNativeSelectBox
            form={form}
            name={name}
            options={options}
            accessors={accessors}
            label={label}
            disableValidation={disableValidation}
            isRequired={rules.length > 0 && isRequiredField(rules)}
            labelClass={labelClass}
            editMode={editMode}
            onChange={onChange}
            tabIndex={tabIndex}
            dataTestId={dataTestId}
          />
        )}
      </Form.Item>
      <HiddenFormInput name={name} rules={rules} />
    </div>
  );
};

export default FormSelectBox;
